import { z } from "zod";
import { SIGNUP_SURVEY_TEAM_SIZES, SIGNUP_SURVEY_USES } from "./domain.js";
import { RUN_ERROR_KINDS, RunTriggerSchema } from "./runs.js";

// No free-form properties: this is also the final allowlist at the external boundary.
export const AnalyticsScreen = z.enum([
  "home",
  "chat",
  "onboarding",
  "settings",
  "integrations",
  "artifacts",
  "board",
  "computer",
  "search",
  "bot_profile",
  "routine",
  "new_bot",
]);
export const AnalyticsSurface = z.enum(["web", "desktop", "ios", "android"]);
export const AnalyticsScreenInput = z
  .object({
    screen: AnalyticsScreen,
    surface: AnalyticsSurface,
  })
  .strict();
export const AnalyticsTrigger = RunTriggerSchema;
/** A coarse failure class; "other" covers everything without a known kind. Never the error text. */
export const AnalyticsErrorKind = z.enum([...RUN_ERROR_KINDS, "other"]);
export const AnalyticsModelConnectMethod = z.enum(["api_key", "oauth", "credits", "compatible"]);
const modelConnect = {
  method: AnalyticsModelConnectMethod,
  // A model catalog provider id, checked against the fixed catalog by the caller.
  provider: z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]{0,63}$/)
    .optional(),
};
export const AnalyticsEvent = z.discriminatedUnion("name", [
  z.object({ name: z.literal("account_created") }).strict(),
  z.object({ name: z.literal("signed_in") }).strict(),
  z.object({ name: z.literal("screen_viewed"), ...AnalyticsScreenInput.shape }).strict(),
  z.object({ name: z.literal("message_sent"), trigger: AnalyticsTrigger }).strict(),
  z.object({ name: z.literal("run_started"), trigger: AnalyticsTrigger }).strict(),
  z.object({ name: z.literal("run_completed"), trigger: AnalyticsTrigger }).strict(),
  z
    .object({
      name: z.literal("run_failed"),
      trigger: AnalyticsTrigger,
      errorKind: AnalyticsErrorKind,
    })
    .strict(),
  z.object({ name: z.literal("model_connect_started"), ...modelConnect }).strict(),
  z.object({ name: z.literal("model_connected"), ...modelConnect }).strict(),
  z
    .object({
      name: z.literal("model_connect_failed"),
      ...modelConnect,
      errorKind: AnalyticsErrorKind,
    })
    .strict(),
  // Only the fixed choices; free-text answers stay in the database.
  z
    .object({
      name: z.literal("signup_survey_answered"),
      skipped: z.boolean(),
      use: z.enum(SIGNUP_SURVEY_USES).optional(),
      teamSize: z.enum(SIGNUP_SURVEY_TEAM_SIZES).optional(),
    })
    .strict(),
]);
export type AnalyticsEvent = z.infer<typeof AnalyticsEvent>;

export type AnalyticsScreen = z.infer<typeof AnalyticsScreen>;
export type AnalyticsErrorKind = z.infer<typeof AnalyticsErrorKind>;
export type AnalyticsModelConnectMethod = z.infer<typeof AnalyticsModelConnectMethod>;
